import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { getInvitationByCode, acceptInvitation } from '@/hooks/useInvitation'
import { getUserFromClerkId } from '@/hooks/useUser'
import { useUser } from "@clerk/clerk-react"
import { BrandedSpinner } from '@/components/BrandedSpinner'
import Button from '@/components/Button'
import { ArrowRight } from 'lucide-react'

export const Route = createFileRoute('/_auth/invite')({
  component: InvitePage,
  validateSearch: (search: Record<string, unknown>): { invitationcode?: string; __clerk_ticket?: string } => ({
    ...(typeof search.invitationcode === 'string' ? { invitationcode: search.invitationcode } : {}),
    ...(typeof search.__clerk_ticket === 'string' ? { __clerk_ticket: search.__clerk_ticket } : {}),
  }),
})

const ACTIVE_STATUSES = new Set(['ACTIVE', 'TRIALING'])

function InvitePage() {
  const navigate = useNavigate()
  const { user: clerkUser } = useUser()
  const { invitationcode } = Route.useSearch() as { invitationcode?: string }
  const { data: invitation, isLoading, isError } = getInvitationByCode(invitationcode || '')
  const { data: appUser, isLoading: userLoading } = clerkUser ? getUserFromClerkId(clerkUser.id) : { data: undefined, isLoading: false }
  const acceptMutation = acceptInvitation()
  const hasTriedAccept = useRef(false)
  const [confirmed, setConfirmed] = useState(false)

  const invitationStatus = invitation?.status
  const isReady = !isLoading && !userLoading && !!invitation && !!appUser

  // Two scenarios where we want explicit consent before auto-accepting:
  //   1. The invitee already has an own active subscription — accepting will
  //      cancel it (with proration credit).
  //   2. The invitee is currently a child of another family plan — accepting
  //      will move them off that plan and onto the new sponsor's instead. The
  //      old admin's seat count drops; both households are affected.
  const hasActiveOwnSub = !!appUser?.subscriptions?.some((sub: any) => {
    const status = String(sub?.status ?? '').toUpperCase()
    return ACTIVE_STATUSES.has(status)
  })
  const isCurrentlyInAnotherFamily =
    !!appUser?.parentUserId && appUser.parentUserId !== invitation?.parentUserId
  const confirmationReason: 'own-sub' | 'in-another-family' | null =
    hasActiveOwnSub ? 'own-sub' : isCurrentlyInAnotherFamily ? 'in-another-family' : null
  const needsConfirmation = isReady && invitationStatus === 'PENDING' && !!confirmationReason && !confirmed

  // Auto-accept the invitation as soon as data is ready, *unless* we're
  // showing the confirmation gate (existing-sub case).
  useEffect(() => {
    if (!isReady) return
    if (hasTriedAccept.current) return
    if (invitationStatus !== 'PENDING') return
    if (appUser?.childUsers && appUser.childUsers.length > 0) return
    if (needsConfirmation) return

    hasTriedAccept.current = true
    acceptMutation.mutate(invitation.invitationCode)
  }, [isReady, invitationStatus, invitation, appUser, needsConfirmation])

  const handleConfirmAndAccept = () => {
    setConfirmed(true)
  }

  const handleContinue = () => {
    window.location.assign('/onboarding')
  }

  // --- Loading state ---
  if (isLoading || userLoading || (invitationStatus === 'PENDING' && !needsConfirmation && !acceptMutation.isSuccess && !acceptMutation.isError)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <BrandedSpinner size="xl" />
        <p className="mt-6 text-lg text-gray-500">Setting up your account...</p>
      </div>
    )
  }

  // --- Family admin trying to accept (edge case) ---
  if (appUser?.childUsers && appUser.childUsers.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Unable to join</h1>
          <p className="text-gray-500 mb-8">
            You can't accept an invitation while you're the admin of an existing family plan.
          </p>
          <Button
            variant="primary"
            size="xlarge"
            fullWidth
            onClick={() => navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } })}
          >
            Go to Home
          </Button>
        </div>
      </div>
    )
  }

  // --- Confirmation gate ---
  if (needsConfirmation) {
    const inviterName = invitation?.fromEmail || 'A family member'

    const headline = `Join ${inviterName}'s family plan?`
    const lead =
      confirmationReason === 'own-sub'
        ? <>Accepting will <strong>cancel your current subscription</strong> and add you to {inviterName}'s plan instead. Any unused time on your current plan will be credited back automatically.</>
        : <>You're currently part of another family plan. Accepting will <strong>move you off that plan</strong> and onto {inviterName}'s plan instead. Your current admin's seat count will adjust to reflect this.</>

    const bullets =
      confirmationReason === 'own-sub'
        ? [
            'Your existing subscription ends right away',
            'You keep all your progress, courses, and data',
            `You're covered by ${inviterName}'s plan from now on`,
          ]
        : [
            "You'll leave your current family plan",
            'You keep all your progress, courses, and data',
            `You'll be covered by ${inviterName}'s plan from now on`,
          ]

    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3 text-center">
            {headline}
          </h1>
          <p className="text-gray-600 mb-6 text-center leading-relaxed">
            {lead}
          </p>
          <div className="rounded-xl bg-slate-50 px-4 py-3 mb-8 text-sm text-slate-600">
            <ul className="space-y-1.5">
              {bullets.map((b) => (
                <li key={b}>• {b}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-3">
            <Button
              variant="primary"
              size="xlarge"
              fullWidth
              onClick={handleConfirmAndAccept}
            >
              Yes, join the family plan
            </Button>
            <Button
              variant="secondary"
              size="xlarge"
              fullWidth
              onClick={() => navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } })}
            >
              Not right now
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // --- Invalid / expired / already used ---
  if (invitationStatus === 'DECLINED' || invitationStatus === 'TIMED_OUT' || invitationStatus === 'CANCELED' || isError || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Invitation expired</h1>
          <p className="text-gray-500 mb-8">
            This invitation is no longer valid. Please ask your family member to send a new one.
          </p>
          <Button
            variant="primary"
            size="xlarge"
            fullWidth
            onClick={() => navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } })}
          >
            Go to Home
          </Button>
        </div>
      </div>
    )
  }

  // --- Accept failed ---
  if (acceptMutation.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
          <p className="text-gray-500 mb-8">
            We couldn't set up your account. Please try again or ask your family member to resend the invitation.
          </p>
          <Button
            variant="primary"
            size="xlarge"
            fullWidth
            onClick={() => {
              hasTriedAccept.current = false
              acceptMutation.reset()
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }

  // --- Success: accepted (or was already accepted) ---
  const inviterName = invitation.fromEmail

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="max-w-md w-full text-center">
        <h1
          className="font-semibold text-gray-900 leading-tight tracking-tight mb-3"
          style={{ fontSize: 'clamp(1.8rem, 5vw, 2.4rem)' }}
        >
          You're all set
        </h1>
        <p className="text-lg text-gray-500 mb-10 leading-relaxed">
          {inviterName} has added you to their family on GranGuard. Let's get you started.
        </p>
        <Button
          variant="primary"
          size="xlarge"
          fullWidth
          onClick={handleContinue}
        >
          Continue <ArrowRight className="w-5 h-5 inline" />
        </Button>
      </div>
    </div>
  )
}
